import * as PIXI from 'pixi.js';
import {GameView} from "../base/GameView";
import {Quadtree} from "./Quadtree";
import {ViewUtils} from "../utils/ViewUtils";

export class GameQuadtreeView extends GameView {

  private _quadtree: Quadtree<PIXI.Container>;
  private _viewport: PIXI.Rectangle;
  private _quadChildren: PIXI.Container[];
  private _childrenPositions: Map<PIXI.Container, number> | null;
  private _invalidated = false;

  constructor(worldSpace: PIXI.Rectangle, maintainOrder: boolean = false) {
    super();
    this._quadtree = new Quadtree<PIXI.Container>(
      worldSpace.left,
      worldSpace.top,
      worldSpace.right,
      worldSpace.bottom
    );
    this._viewport = worldSpace.clone();
    this._quadChildren = [];
    this._childrenPositions = maintainOrder ? new Map() : null;
    this._invalidated = true;
  }

  override advanceTime(delta:number): void {
    if (!this._invalidated) {
      return;
    }

    this._invalidated = false;
    this.removeChildren();

    const visibleObjects = this._quadtree.objectsInRectangle(this._viewport);

    // Если нужно сохранить порядок, сортируем видимые объекты
    if (this._childrenPositions) {
      visibleObjects.sort((a, b) => {
        const posA = this._childrenPositions!.get(a) ?? 0;
        const posB = this._childrenPositions!.get(b) ?? 0;
        return posA - posB;
      });
    }

    for (const visibleObject of visibleObjects) {
      super.addChildAt(visibleObject, this.children?.length || 0);
    }
  }

  public updateChild(child: PIXI.Container): void {
    this._quadtree.update(child, child.getBounds().rectangle);
    this._invalidated = true;
  }

  override addChild<U extends (PIXI.IRenderLayer | PIXI.ContainerChild)[]>(...children: U): U[0] {
    return this.addChildAt(children[0], this._quadChildren.length);
  }

  override addChildAt<U extends PIXI.IRenderLayer | PIXI.ContainerChild>(child: U, index: number): U {
    // Проверяем пересечение с viewport
    if (child instanceof PIXI.Container) {
      if (this._viewport.intersects(child.getBounds().rectangle)) {
        super.addChildAt(child, this.children.length);
      }

      if (this._childrenPositions) {
        this._quadChildren.splice(index, 0, child);
        this.updateChildrenPositions(index);
      } else {
        this._quadChildren.push(child);
      }

      this._quadtree.insert(child, child.getBounds().rectangle);
      this._invalidated = true;
    }

    return child;
  }

  override removeChild<U extends (PIXI.IRenderLayer | PIXI.ContainerChild)[]>(...children: U): U[0] {
    const child = children[0];

    if(child instanceof PIXI.Container) {
      const index = this._quadChildren.indexOf(child);
      if (index >= 0) {
        this._quadChildren.splice(index, 1);
      }

      this._quadtree.remove(child);

      if (this._childrenPositions) {
        this._childrenPositions.delete(child);
        this.updateChildrenPositions(index);
      }

      if (this.children.includes(child)) {
        super.removeChild(child);
      }
    }

    return child;
  }

  public get visibleViewport(): PIXI.Rectangle {
    return this._viewport;
  }

  public get quadtree() {
    return this._quadtree;
  }

  override get width(): number {
    return 0;
  }

  override get height(): number {
    return 0;
  }

  public updateViewport(viewport: PIXI.Rectangle, force: boolean = false): void {
    if (!force && ViewUtils.isEqualRects(viewport, this._viewport)) {
      return;
    }
    this._viewport.copyFrom(viewport);
    this._invalidated = true;
  }

  private updateChildrenPositions(startIndex: number): void {
    if (!this._childrenPositions) return;

    for (let i = startIndex; i < this._quadChildren.length; i++) {
      const child = this._quadChildren[i];
      this._childrenPositions.set(child, i);
    }
  }
}
